import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Printer, CreditCard } from "lucide-react";
import { PortalHeader, PortalCard } from "@/components/portal/PortalHeader";
import { getMyInvoice, getAccountingSettings } from "@/lib/accounting/accounting.functions";
import { fmtDate, fmtMoney } from "@/lib/accounting/exports";
import { InvoicePayModal } from "@/components/portal/InvoicePayModal";
import { paymentsConfigured } from "@/lib/stripe";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/invoices/$id")({
  component: PortalInvoiceDetail,
});

function PortalInvoiceDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const fn = useServerFn(getMyInvoice);
  const sFn = useServerFn(getAccountingSettings);
  const { data, isLoading } = useQuery({ queryKey: ["portal", "invoice", id], queryFn: () => fn({ data: { id } }) });
  const settings = useQuery({ queryKey: ["portal", "acct-settings"], queryFn: () => sFn().catch(() => null) });
  const [payOpen, setPayOpen] = useState(false);

  // If Stripe redirected back with ?paid=1, refresh & confirm
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "1") {
      toast.success("Payment received. Thank you!");
      qc.invalidateQueries({ queryKey: ["portal", "invoice", id] });
      // Poll a few times in case webhook is still processing
      const t1 = setTimeout(() => qc.invalidateQueries({ queryKey: ["portal", "invoice", id] }), 2500);
      const t2 = setTimeout(() => qc.invalidateQueries({ queryKey: ["portal", "invoice", id] }), 6000);
      const url = new URL(window.location.href);
      url.searchParams.delete("paid");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [id, qc]);

  if (isLoading || !data) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  const { invoice, line_items, payments } = data;
  const balanceDue = Number(invoice.balance_due);
  const canPayOnline = paymentsConfigured() && balanceDue > 0 && invoice.status !== "void";

  return (
    <>
      <Link to="/portal/invoices" className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1">← Invoices</Link>
      <PortalHeader eyebrow={`Invoice ${invoice.invoice_number}`} title={fmtMoney(invoice.total)} description={`Issued ${fmtDate(invoice.issue_date)} · Due ${fmtDate(invoice.due_date)}`} />

      <div className="flex gap-2 mb-4">
        {canPayOnline ? (
          <button
            onClick={() => setPayOpen(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-amber-300 text-zinc-950 text-xs font-semibold hover:bg-amber-200"
          >
            <CreditCard className="size-3.5" /> Pay {fmtMoney(balanceDue)} online
          </button>
        ) : null}
        <button onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-200 hover:bg-white/5"><Printer className="size-3.5" /> Print / Save PDF</button>
      </div>

      <PortalCard className="p-5 mb-4">
        <div className="space-y-2 text-sm">
          {line_items.map((l: any) => (
            <div key={l.id} className="flex justify-between border-b border-white/5 pb-2">
              <div>
                <p className="text-white">{l.description}</p>
                <p className="text-[11px] text-zinc-500 capitalize">{l.kind.replace("_", " ")} · {Number(l.quantity)} × {fmtMoney(l.unit_price)}</p>
              </div>
              <p className="text-white font-medium">{fmtMoney(l.amount)}</p>
            </div>
          ))}
          <div className="space-y-1 pt-2">
            <Row k="Subtotal" v={fmtMoney(invoice.subtotal)} />
            <Row k={`Tax (${(Number(invoice.tax_rate) * 100).toFixed(2)}%)`} v={fmtMoney(invoice.tax_amount)} />
            <Row k="Total" v={fmtMoney(invoice.total)} bold />
            <Row k="Paid" v={fmtMoney(invoice.amount_paid)} />
            <Row k="Balance due" v={fmtMoney(invoice.balance_due)} bold accent />
          </div>
        </div>
      </PortalCard>

      <PortalCard className="p-5 mb-4">
        <p className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold mb-2">Payment instructions</p>
        <p className="text-sm text-zinc-300 whitespace-pre-wrap">
          {settings.data?.payment_instructions ?? "Please pay by check, ACH, Zelle, or Venmo. Contact us for ACH details."}
        </p>
        {canPayOnline ? (
          <p className="text-xs text-zinc-500 mt-3 italic">Or click "Pay online" above to pay securely by card.</p>
        ) : (
          <p className="text-xs text-zinc-500 mt-3 italic">Online payments are not enabled yet. Please follow the payment instructions above.</p>
        )}
      </PortalCard>

      <h2 className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Payment history</h2>
      {payments.length === 0 ? (
        <p className="text-xs text-zinc-500">No payments recorded yet.</p>
      ) : (
        <div className="space-y-2">
          {payments.map((p: any, i: number) => (
            <PortalCard key={i} className="p-3 flex justify-between items-center text-sm">
              <div>
                <p className="text-white">{fmtMoney(p.amount)} <span className="text-zinc-500 text-xs capitalize">· {p.method?.replace?.("_", " ")}</span></p>
                <p className="text-[11px] text-zinc-500">{fmtDate(p.recorded_at)} {p.reference ? `· ${p.reference}` : ""}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">{p.category ?? p.kind}</span>
            </PortalCard>
          ))}
        </div>
      )}
      {payOpen ? <InvoicePayModal invoiceId={id} onClose={() => setPayOpen(false)} /> : null}
    </>
  );
}

function Row({ k, v, bold, accent }: { k: string; v: string; bold?: boolean; accent?: boolean }) {
  return <div className={`flex justify-between ${bold ? "text-white font-semibold" : "text-zinc-400"} ${accent ? "text-amber-300" : ""}`}><span>{k}</span><span>{v}</span></div>;
}